import { kebabCase } from 'change-case';
import {
  Suspense,
  type FunctionComponent,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import _UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import _UFOSegment from '@atlaskit/react-ufo/segment';

/**
 * Props for the UFOSuspense component.
 */
type UFOSuspenseProps = {
  /**
   * The name identifier for this UFO suspense boundary.
   * Used for tracking and identifying this segment in UFO metrics.
   */
  name: string;

  /**
   * The fallback component to display while the suspended content is loading.
   * This can be any React node (component, element, string, etc.).
   */
  fallback: ReactNode;
};

/**
 * A UFO-aware Suspense component that integrates React Suspense with UFO performance tracking.
 *
 * This component provides a drop-in replacement for React's Suspense component that automatically
 * integrates with UFO v2 performance monitoring. When UFO is enabled, it uses UFO to track loading
 * states and performance metrics. When UFO is disabled, it falls back to standard React Suspense behavior.
 *
 * @param children - The content that may suspend (typically lazy-loaded components)
 * @param name - The name identifier for UFO tracking
 * @param fallback - The fallback UI to show while loading
 *
 * @example
 * ```tsx
 * const LazyComponent = lazy(() => import('./LazyComponent'));
 *
 * function App() {
 *   return (
 *     <UFOSuspense
 *       name="lazy-dashboard"
 *       fallback={<div>Loading dashboard...</div>}
 *     >
 *       <LazyComponent />
 *     </UFOSuspense>
 *   );
 * }
 * ```
 */
export const UFOSuspense: FunctionComponent<
  PropsWithChildren<UFOSuspenseProps>
> = ({ children, name, fallback }) => {
  return (
    <_UFOSegment name={kebabCase(name)}>
      <Suspense
        fallback={
          <>
            <_UFOLoadHold name={kebabCase(name)} />
            {fallback}
          </>
        }
      >
        {children}
      </Suspense>
    </_UFOSegment>
  );
};
